import React, { useState, useEffect } from 'react';
import '../styles/CookieConsent.css';
import analytics from '../services/analytics';
import { db, doc, getDoc } from '../services/firebase';

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [cookieSettings, setCookieSettings] = useState(null);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true, can't be disabled
    analytics: false,
    marketing: false,
    functional: false
  });

  useEffect(() => {
    // Load cookie settings from Firebase first
    loadCookieSettings();
  }, []);

  const loadCookieSettings = async () => {
    try {
      const settingsRef = doc(db, 'settings', 'global');
      const settingsSnap = await getDoc(settingsRef);
      
      if (settingsSnap.exists()) {
        const data = settingsSnap.data();
        const cookieConfig = data.cookieConsent;
        
        if (cookieConfig) {
          setCookieSettings(cookieConfig);
          
          // Set default preferences based on admin settings
          setPreferences({
            necessary: true,
            analytics: cookieConfig.analyticsEnabled ?? false,
            marketing: cookieConfig.marketingEnabled ?? false,
            functional: false
          });
          
          // Check if user has already made a choice
          const hasConsent = localStorage.getItem('grey-cookie-consent');
          if (!hasConsent && cookieConfig.enabled) {
            // Show banner after a short delay for better UX
            setTimeout(() => setShowBanner(true), 1000);
          } else if (hasConsent) {
            // Load saved preferences
            const savedPreferences = JSON.parse(hasConsent);
            setPreferences(savedPreferences);
            initializeAnalytics(savedPreferences);
          }
        }
      }
    } catch (err) {
      console.error('Error loading cookie settings:', err);
      // Fallback to default behavior if Firebase fails
      const hasConsent = localStorage.getItem('grey-cookie-consent');
      if (!hasConsent) {
        setTimeout(() => setShowBanner(true), 1000);
      } else {
        const savedPreferences = JSON.parse(hasConsent);
        setPreferences(savedPreferences);
        initializeAnalytics(savedPreferences);
      }
    }
  };

  const initializeAnalytics = (prefs) => {
    if (prefs.analytics) {
      // Initialize Google Analytics or other analytics
      initGoogleAnalytics();
    }
    if (prefs.marketing) {
      // Initialize marketing pixels (Facebook, etc.)
      initMarketingPixels();
    }
    if (prefs.functional) {
      // Initialize functional cookies (chat widgets, etc.)
      initFunctionalCookies();
    }
  };

  const initGoogleAnalytics = () => {
    // Example Google Analytics 4 implementation
    window.dataLayer = window.dataLayer || [];
    function gtag(){window.dataLayer.push(arguments);}
    window.gtag = gtag;
    gtag('js', new Date());
    gtag('config', 'GA_MEASUREMENT_ID'); // Replace with your GA4 ID
    
    // Track page views
    gtag('event', 'page_view', {
      page_title: document.title,
      page_location: window.location.href
    });
  };

  const initMarketingPixels = () => {
    // Facebook Pixel example
    (function(f,b,e,v,n,t,s)
    {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)})(window, document,'script',
    'https://connect.facebook.net/en_US/fbevents.js');
    
    if (window.fbq) {
      window.fbq('init', 'YOUR_PIXEL_ID'); // Replace with your pixel ID
      window.fbq('track', 'PageView');
    }
  };

  const initFunctionalCookies = () => {
    // Initialize chat widgets, user preferences, etc.
    console.log('Functional cookies initialized');
  };

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
      functional: true
    };
    setPreferences(allAccepted);
    savePreferences(allAccepted);
    setShowBanner(false);
  };

  const handleRejectAll = () => {
    const onlyNecessary = {
      necessary: true,
      analytics: false,
      marketing: false,
      functional: false
    };
    setPreferences(onlyNecessary);
    savePreferences(onlyNecessary);
    setShowBanner(false);
  };

  const handleCustomize = () => {
    setShowSettings(true);
  };

  const handleSaveCustom = () => {
    savePreferences(preferences);
    setShowBanner(false);
    setShowSettings(false);
  };

  const savePreferences = (prefs) => {
    localStorage.setItem('grey-cookie-consent', JSON.stringify(prefs));
    localStorage.setItem('grey-cookie-consent-date', new Date().toISOString());
    initializeAnalytics(prefs);
    
    // Update analytics service with new consent
    analytics.updateConsent(prefs);
    
    // Track consent choice
    analytics.trackEvent('Privacy', 'cookie_consent', 'preferences_saved', Object.values(prefs).filter(Boolean).length);
  };

  const handlePreferenceChange = (category) => {
    if (category === 'necessary') return; // Can't change necessary cookies
    
    setPreferences(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  if (!showBanner || !cookieSettings) return null;

  return (
    <>
      {/* Cookie Banner */}
      <div className="cookie-banner">
        <div className="cookie-container">
          <div className="cookie-content">
            <div className="cookie-icon">
              🍪
            </div>
            <div className="cookie-text">
              <h3>We value your privacy</h3>
              <p>
                {cookieSettings.bannerText}
              </p>
            </div>
            <div className="cookie-actions">
              <button 
                className="cookie-btn cookie-btn-secondary"
                onClick={handleRejectAll}
              >
                {cookieSettings.declineButtonText}
              </button>
              <button 
                className="cookie-btn cookie-btn-outline"
                onClick={handleCustomize}
              >
                {cookieSettings.settingsButtonText}
              </button>
              <button 
                className="cookie-btn cookie-btn-primary"
                onClick={handleAcceptAll}
              >
                {cookieSettings.acceptButtonText}
              </button>
              {cookieSettings.privacyPolicyUrl && (
                <a 
                  href={cookieSettings.privacyPolicyUrl}
                  className="cookie-privacy-link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Privacy Policy
                </a>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Cookie Settings Modal */}
      {showSettings && (
        <div className="cookie-modal-overlay">
          <div className="cookie-modal">
            <div className="cookie-modal-header">
              <h2>Cookie Preferences</h2>
              <button 
                className="cookie-close"
                onClick={() => setShowSettings(false)}
              >
                ×
              </button>
            </div>
            
            <div className="cookie-modal-content">
              <p className="cookie-description">
                Choose which cookies you want to accept. You can change these settings at any time.
              </p>

              <div className="cookie-categories">
                {/* Necessary Cookies */}
                <div className="cookie-category">
                  <div className="cookie-category-header">
                    <label className="cookie-switch">
                      <input 
                        type="checkbox" 
                        checked={preferences.necessary}
                        disabled={true}
                        readOnly
                      />
                      <span className="cookie-slider necessary"></span>
                    </label>
                    <div>
                      <h4>Necessary Cookies</h4>
                      <p>Required for the website to function properly. Cannot be disabled.</p>
                    </div>
                  </div>
                  <div className="cookie-details">
                    <small>These cookies are essential for the website to function and cannot be switched off in our systems.</small>
                  </div>
                </div>

                {/* Analytics Cookies */}
                <div className="cookie-category">
                  <div className="cookie-category-header">
                    <label className="cookie-switch">
                      <input 
                        type="checkbox" 
                        checked={preferences.analytics}
                        onChange={() => handlePreferenceChange('analytics')}
                      />
                      <span className="cookie-slider"></span>
                    </label>
                    <div>
                      <h4>Analytics Cookies</h4>
                      <p>Help us understand how visitors interact with our website.</p>
                    </div>
                  </div>
                  <div className="cookie-details">
                    <small>These cookies allow us to count visits and traffic sources so we can measure and improve the performance of our site.</small>
                  </div>
                </div>

                {/* Marketing Cookies */}
                <div className="cookie-category">
                  <div className="cookie-category-header">
                    <label className="cookie-switch">
                      <input 
                        type="checkbox" 
                        checked={preferences.marketing}
                        onChange={() => handlePreferenceChange('marketing')}
                      />
                      <span className="cookie-slider"></span>
                    </label>
                    <div>
                      <h4>Marketing Cookies</h4>
                      <p>Used to track visitors across websites for advertising purposes.</p>
                    </div>
                  </div>
                  <div className="cookie-details">
                    <small>These cookies may be set through our site by our advertising partners to build a profile of your interests.</small>
                  </div>
                </div>

                {/* Functional Cookies */}
                <div className="cookie-category">
                  <div className="cookie-category-header">
                    <label className="cookie-switch">
                      <input 
                        type="checkbox" 
                        checked={preferences.functional}
                        onChange={() => handlePreferenceChange('functional')}
                      />
                      <span className="cookie-slider"></span>
                    </label>
                    <div>
                      <h4>Functional Cookies</h4>
                      <p>Enable enhanced functionality and personalization.</p>
                    </div>
                  </div>
                  <div className="cookie-details">
                    <small>These cookies enable the website to provide enhanced functionality and personalization like chat widgets and social media features.</small>
                  </div>
                </div>
              </div>
            </div>

            <div className="cookie-modal-footer">
              <button 
                className="cookie-btn cookie-btn-outline"
                onClick={() => setShowSettings(false)}
              >
                Cancel
              </button>
              <button 
                className="cookie-btn cookie-btn-primary"
                onClick={handleSaveCustom}
              >
                Save Preferences
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CookieConsent; 